'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLeadFormGuardContext } from '@ps/features/access-control/components/access-guard/lead-form-guard-context';
import { createGuestAccount } from '@ps/functions/guest-auth';
import { LeadFormTriggerActionType } from '@ps/models';
import type { CreatorInfo } from '@ps/types/creator';
import { useCurrentUserId } from '@ps/ui/contexts/auth';
import { AuthProvider } from '@ps/ui/contexts/auth/provider';

import { useCalendarBookingContext } from '../context/provider';
import type {
  MeetingSlot,
  MeetingTypeItem,
} from '../hooks/use-available-dates';
import { DateSelectorSkeleton } from '../skeleton';
import { bookMeetingSlot } from '../utils/book-meeting-slot';
import { BookButton, WiredBookButton } from './book-button';
import { BookingSuccessModal } from './booking-success-modal';
import { DateSelector } from './date-selector';
import { MeetingTypeCard } from './meeting-type-card';

// ─── Free Meeting Booking API ─────────────────────────────────────────────

// ─── Props ────────────────────────────────────────────────────────────────

export interface BookingViewProps {
  storeId: string | undefined;
  /** Listing ID — used for listing-level lead form trigger (takes priority over meeting-type trigger). */
  listingId?: string;
  /** Seller profile for access guard modals. */
  sellerProfile: CreatorInfo;
  /** Enable active "Book" button + booking flow. Only for public view. */
  enableBooking?: boolean;
  /** Compact mode for smaller containers (e.g. chat bubble popups). */
  compact?: boolean;
  /** Called after a meeting is successfully booked. */
  onBookingSuccess?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────

export function BookingView({
  storeId,
  listingId,
  sellerProfile,
  enableBooking = false,
  compact = false,
  onBookingSuccess,
}: BookingViewProps) {
  const { isLoaded: leadFormsLoaded } = useLeadFormGuardContext();
  const currentUserId = useCurrentUserId();

  const ctx = useCalendarBookingContext();
  if (!ctx) {
    throw new Error('BookingView must be wrapped in a CalendarBookingProvider');
  }
  const {
    dates,
    isLoading,
    isLoadingMore,
    loadMoreDates,
    fetchMeetingTypes,
    timezone,
    onBooked,
  } = ctx;

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypeItem[]>([]);
  const [selectedType, setSelectedType] = useState<MeetingTypeItem | null>(
    null,
  );
  const [selectedSlot, setSelectedSlot] = useState<MeetingSlot | null>(null);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset selection in ALL widgets when any widget completes a booking
  const bookingVersion = ctx.bookingVersion;
  const prevVersionRef = useRef(bookingVersion);
  useEffect(() => {
    if (prevVersionRef.current !== bookingVersion) {
      prevVersionRef.current = bookingVersion;
      if (!showSuccess) {
        setSelectedDate(null);
        setSelectedType(null);
        setSelectedSlot(null);
        setMeetingTypes([]);
      }
    }
  }, [bookingVersion, showSuccess]);

  // ── Date selection ───────────────────────────────────────────────────
  const handleDateSelect = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      setSelectedType(null);
      setSelectedSlot(null);
      setIsLoadingTypes(true);

      fetchMeetingTypes(date)
        .then(setMeetingTypes)
        .catch(() => setMeetingTypes([]))
        .finally(() => setIsLoadingTypes(false));
    },
    [fetchMeetingTypes],
  );

  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  const handleTypeSelect = useCallback(
    (mt: MeetingTypeItem) => {
      setSelectedType(mt);
      setSelectedSlot(null);
      setIsLoadingSlots(true);

      if (selectedDate) {
        fetchMeetingTypes(selectedDate)
          .then(freshTypes => {
            setMeetingTypes(freshTypes);
            const updated = freshTypes.find(t => t.id === mt.id);
            setSelectedType(updated ?? null);
          })
          .catch(() => {})
          .finally(() => setIsLoadingSlots(false));
      }
    },
    [selectedDate, fetchMeetingTypes],
  );

  const handleChangeType = useCallback(() => {
    setSelectedType(null);
    setSelectedSlot(null);
  }, []);

  const handleSlotSelect = useCallback((slot: MeetingSlot) => {
    setSelectedSlot(slot);
  }, []);

  // ── Book meeting ─────────────────────────────────────────────────────
  const doBooking = useCallback(async () => {
    if (!storeId || !selectedType || !selectedSlot) return;
    // Auth loads lazily — `currentUserId` is `undefined` until it resolves.
    // Ignore the tap until then, else a signed-in viewer would be misread as
    // anonymous and a throwaway guest minted for them (booking mis-attributed).
    if (currentUserId === undefined) return;
    setIsBooking(true);
    try {
      const userId = currentUserId;
      if (!userId) {
        await createGuestAccount();
      }
      await bookMeetingSlot({
        storeId,
        meetingTypeId: selectedType.id,
        startTime: selectedSlot.startTime,
        duration: selectedType.durationMinutes,
        timezone,
      });
      setShowSuccess(true);
      onBooked();
      onBookingSuccess?.();
    } catch (error) {
      console.error('[BookingView] Booking failed:', error);
    } finally {
      setIsBooking(false);
    }
  }, [
    storeId,
    selectedType,
    selectedSlot,
    timezone,
    onBooked,
    onBookingSuccess,
    currentUserId,
  ]);

  const handleSuccessDone = useCallback(() => {
    setShowSuccess(false);
    setSelectedDate(null);
    setSelectedType(null);
    setSelectedSlot(null);
    setMeetingTypes([]);
  }, []);

  const canBook = selectedSlot != null && selectedType != null;

  // ── Render ───────────────────────────────────────────────────────────

  // Show the date-selector skeleton while lead forms are being fetched
  // (public view only) — consistent with the lazy-load fallback.
  if (enableBooking && !leadFormsLoaded) {
    return <DateSelectorSkeleton compact={compact} />;
  }

  return (
    <AuthProvider>
      <div className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-3'}`}>
        {/* Date selector */}
        <DateSelector
          dates={dates}
          selectedDate={selectedDate}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          compact={compact}
          onDateSelect={handleDateSelect}
          onScrollEnd={loadMoreDates}
        />

        {/* Meeting types (shown after date selection) */}
        {selectedDate && (
          <div className={compact ? 'space-y-1.5' : 'space-y-3'}>
            {isLoadingTypes ? (
              <div
                className={`flex items-center justify-center ${compact ? 'py-2' : 'py-4'}`}
              >
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              </div>
            ) : meetingTypes.length > 0 ? (
              selectedType ? (
                <MeetingTypeCard
                  meetingType={selectedType}
                  isSelected
                  isLoadingSlots={isLoadingSlots}
                  selectedSlot={selectedSlot}
                  showChange={meetingTypes.length > 1}
                  compact={compact}
                  onSlotSelect={handleSlotSelect}
                  onChangeType={handleChangeType}
                />
              ) : (
                meetingTypes.map(mt => (
                  <MeetingTypeCard
                    key={mt.id}
                    meetingType={mt}
                    isSelected={false}
                    isLoadingSlots={false}
                    selectedSlot={null}
                    showChange={false}
                    compact={compact}
                    onSelect={handleTypeSelect}
                    onSlotSelect={handleSlotSelect}
                  />
                ))
              )
            ) : (
              <p
                className={`force-light:text-gray-400 py-2 text-center text-gray-400 dark:text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                No meeting types available for this date.
              </p>
            )}
          </div>
        )}

        {/* Book button — with access guard for public view (hidden for store owner) */}
        {canBook &&
        enableBooking &&
        storeId &&
        selectedType &&
        currentUserId !== storeId ? (
          <WiredBookButton
            storeId={storeId}
            triggers={[
              // Listing-level lead form takes priority over meeting-type
              ...(listingId
                ? [
                    {
                      type: LeadFormTriggerActionType.RealEstateListing,
                      id: listingId,
                    },
                  ]
                : []),
              {
                type: LeadFormTriggerActionType.Calendar,
                id: selectedType.id,
              },
            ]}
            sellerProfile={sellerProfile}
            skipFollowGuard
            isBooking={isBooking}
            compact={compact}
            onBook={() => void doBooking()}
          />
        ) : canBook ? (
          <BookButton disabled compact={compact} />
        ) : null}

        {/* Success modal */}
        <BookingSuccessModal
          open={showSuccess}
          selectedDate={selectedDate}
          selectedType={selectedType}
          selectedSlot={selectedSlot}
          onOpenChange={setShowSuccess}
          onDone={handleSuccessDone}
        />
      </div>
    </AuthProvider>
  );
}
